// eslint-disable-next-line no-unused-vars
import React, { useState, useRef, useEffect } from 'react';
import { IoWarningOutline } from 'react-icons/io5';
import { db, storage, auth } from '../../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { Camera } from '@capacitor/camera'; // Import Capacitor Camera
import { Capacitor } from '@capacitor/core';
import './BotonPanico.css';

const BotonPanico = ({ onActivation }) => {
    // eslint-disable-next-line no-unused-vars
    const [active, setActive] = useState(false);
    const [recording, setRecording] = useState(false);
    const [fillHeight, setFillHeight] = useState(0);

    // eslint-disable-next-line no-unused-vars
    const timerRef = useRef(null);
    const progressInterval = useRef(null);
    const startTime = useRef(null);
    const mediaRecorder = useRef(null);

    // --- ACTIONS ---

    const startHold = (e) => {
        // Prevent default to avoid context menus on long press
        if (e.cancelable) e.preventDefault();
        if (recording) return;

        setActive(true);
        startTime.current = Date.now();

        // Start progress animation
        progressInterval.current = setInterval(() => {
            const elapsed = Date.now() - startTime.current;
            const percent = Math.min((elapsed / 3000) * 100, 100);
            setFillHeight(percent);

            if (percent >= 100) {
                triggerPanic();
                stopHold(); // clear intervals but keep active state logic handled by triggerPanic
            }
        }, 50);
    };

    const stopHold = () => {
        if (!recording) {
            clearInterval(progressInterval.current);
            setFillHeight(0);
            setActive(false);
        } else {
            // If recording, we don't reset visual immediately, logic handled elsewhere
            clearInterval(progressInterval.current);
        }
    };

    const triggerPanic = async () => {
        // Vibrate device if supported
        if (navigator.vibrate) navigator.vibrate([500, 500, 500]);

        setRecording(true);
        setActive(true); // Keep UI active
        setFillHeight(100);

        // 1. Create Initial Alert
        let alertId = null;
        try {
            const docRef = await addDoc(collection(db, 'alerts'), {
                type: 'panic_button',
                userId: auth.currentUser?.uid || 'anonymous',
                userName: auth.currentUser?.displayName || 'Residente',
                timestamp: serverTimestamp(),
                status: 'active',
                centinelaTriggered: true, // Signal for CCTV
                evidenceUrl: null
            });
            alertId = docRef.id;
            console.log("Panic Alert Created:", alertId);
            if (onActivation) onActivation(alertId);
        } catch (e) {
            console.error("Error creating alert:", e);
            alert("Error al enviar alerta. Llame al 911.");
        }

        // 2. Start Recording Evidence
        await startRecording(alertId);
    };

    const startRecording = async (currentAlertId) => {
        // NATIVE PERMISSION CHECK
        if (Capacitor.isNativePlatform()) {
            try {
                const permissions = await Camera.checkPermissions();
                if (permissions.camera !== 'granted' || permissions.microphone !== 'granted') {
                    const newPerms = await Camera.requestPermissions({ permissions: ['camera', 'microphone'] });
                    if (newPerms.camera !== 'granted') {
                        alert("Se necesita permiso de cámara para grabar evidencia.");
                        return;
                    }
                }
            } catch (err) {
                console.warn("Capacitor camera permission check failed", err);
            }
        }

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.warn("Media devices not supported");
            // Still alert was sent, just no video
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: { facingMode: "user" } });
            const recorder = new MediaRecorder(stream);
            const localChunks = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) localChunks.push(e.data);
            };

            recorder.onstop = async () => {
                // Upload
                const blob = new Blob(localChunks, { type: 'video/webm' });
                await uploadEvidence(blob, currentAlertId);

                // Stop stream tracks
                stream.getTracks().forEach(track => track.stop());
                setRecording(false);
                setActive(false);
                setFillHeight(0);
                alert("Alerta Enviada. Grabación de evidencia completada.");
            };

            recorder.start();
            mediaRecorder.current = recorder;

            // Stop automatically after 10 seconds
            setTimeout(() => {
                if (recorder.state === 'recording') recorder.stop();
            }, 10000);

        } catch (err) {
            console.error("Error accessing camera:", err);
            setRecording(false);
            setActive(false);
            alert("No se pudo acceder a la cámara. La alerta se envió sin video.");
        }
    };

    const uploadEvidence = async (blob, alertId) => {
        if (!alertId) return;

        const fileName = `evidence/panic_${auth.currentUser?.uid}_${Date.now()}.webm`;
        const storageRef = ref(storage, fileName);
        try {
            await uploadBytes(storageRef, blob);
            console.log("Evidence uploaded:", fileName);
            // In real app: Update the alert document with the download URL
            // For now, we assume backend listener might pick it up differently or separate task
        } catch (e) {
            console.error("Upload failed", e);
        }
    };

    return (
        <div className="boton-panico-container">
            <div
                className={`boton-panico ${recording ? 'recording' : ''}`}
                onMouseDown={startHold}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                onTouchStart={startHold}
                onTouchEnd={stopHold}
                onTouchCancel={stopHold}
                onContextMenu={(e) => e.preventDefault()}
                style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
                title="Mantener 3s para SOS"
            >
                <div className="boton-content">
                    <IoWarningOutline size={32} color="white" />
                    <span className="boton-label">
                        {recording ? "GRABANDO" : "SOS"}
                    </span>
                </div>

                {/* Progress Fill */}
                <div className="progress-ring" style={{ height: `${fillHeight}%` }} />
            </div>
        </div>
    );
};

export default BotonPanico;
